import type { Status } from '@usealloy/component-library';
import { accessTypeLabelMap } from '~/dashboard/admin/provisioning/helpers/constants';
import { RBACAccessType } from '~/dashboard/admin/provisioning/types';

/**
 * `getTableData` (reused for this section, see useAllCustomersData) already
 * converts each row's RBACAccessType into its display label via
 * `accessTypeLabelMap` — so we key the Badge status map off those same
 * labels rather than the raw enum, to stay in sync with the data we're given.
 */
export const accessLabelBadgeStatusMap: Record<string, Status> = {
  [accessTypeLabelMap[RBACAccessType.None]]: 'neutral',
  [accessTypeLabelMap[RBACAccessType.Pending]]: 'attention',
  [accessTypeLabelMap[RBACAccessType.Temporary]]: 'info',
  [accessTypeLabelMap[RBACAccessType.Permanent]]: 'success',
};

/**
 * The switcher's highlight background, shared by every highlightable target in
 * the popover — `SwitchableAgentButton`, `CustomerRow`, and the "Customer List"
 * footer — so keyboard highlighting and `:hover` look identical across all
 * three. `CustomerRow` applies it through styled-components; the others apply it
 * inline, which is why it lives here rather than in either module.
 */
export const HIGHLIGHT_BACKGROUND = 'oklch(from #EBEBE6 calc(l * .95) c h)';

/**
 * Floor for the switcher popover's width, and so for the virtualized rows —
 * `AutoSizer` measures the popover and the rows follow, never dropping below
 * this (see AllCustomersSection).
 *
 * FE-P's switcher declares `minWidth: 250` and then lets `Popover.Content` grow
 * to fit its content — measured at ~250px for short customer names and ~380px
 * for long ones, roughly the widest row's text plus ~104px of row chrome. Daffy
 * can't size to content the same way (react-virtualized needs a number up
 * front), so this picks a fixed width in the middle of FE-P's observed range,
 * wide enough to show names like "Alloy Test Organization" untruncated.
 */
export const ALL_CUSTOMERS_POPOVER_MIN_WIDTH = 320;
export const ALL_CUSTOMERS_ROW_HEIGHT = 40;
export const ALL_CUSTOMERS_MAX_LIST_HEIGHT = 320; // ~8 visible rows before scrolling
